// Token storage — D-WEB-007 (INTERFACES sec 2.1):
//   access_token em MEMORIA (XSS-resistant durante runtime)
//   refresh_token em localStorage (debito XSS conhecido; mitigado por CSP estrito + 30d TTL)
//   user data espelhado em localStorage pra hydrate inicial sem flash de login
import type { AuthUser } from "./types";

const LS_REFRESH = "catalog.refresh_token";
const LS_USER = "catalog.user";

interface AuthState {
  access: string | null;
  user: AuthUser | null;
}

let memory: AuthState = { access: null, user: null };
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

export function getAccessToken(): string | null {
  return memory.access;
}

export function getUser(): AuthUser | null {
  return memory.user;
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(LS_REFRESH);
  } catch {
    return null;
  }
}

export function setSession(access: string, refresh: string, user: AuthUser): void {
  memory = { access, user };
  try {
    localStorage.setItem(LS_REFRESH, refresh);
    localStorage.setItem(LS_USER, JSON.stringify(user));
  } catch {
    /* ignore */
  }
  emit();
}

/**
 * Atualiza SO o usuario (papel/capabilities/canais) sem tocar tokens — usado
 * pelo refresh periodico de /auth/me (RBAC configuravel, W1). Diferente de
 * setSession: nao existe sessao pra iniciar aqui, so o perfil pode ter
 * mudado desde o login (causa raiz do "admin nao alcanca Categorias":
 * mudar o perfil de alguem no banco nao mudava o que a sessao aberta via
 * ate ela deslogar). No-op se nao houver sessao ativa (usuario deslogou
 * entre o disparo da chamada e a resposta).
 */
export function setUser(user: AuthUser): void {
  if (!memory.user) return;
  memory = { ...memory, user };
  try {
    localStorage.setItem(LS_USER, JSON.stringify(user));
  } catch {
    /* ignore */
  }
  emit();
}

export function updateAccessOnly(access: string, refresh: string): void {
  memory = { ...memory, access };
  try {
    localStorage.setItem(LS_REFRESH, refresh);
  } catch {
    /* ignore */
  }
  emit();
}

export function clearSession(): void {
  memory = { access: null, user: null };
  try {
    localStorage.removeItem(LS_REFRESH);
    localStorage.removeItem(LS_USER);
  } catch {
    /* ignore */
  }
  emit();
}

export function hydrateFromStorage(): void {
  try {
    const u = localStorage.getItem(LS_USER);
    if (u) {
      memory = { access: null, user: JSON.parse(u) as AuthUser };
    }
  } catch {
    /* ignore */
  }
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getSnapshot(): AuthState {
  return memory;
}
